import { ComponentMultiStyleConfig } from '@chakra-ui/react';
import type { StyleFunctionProps } from '@chakra-ui/theme-tools';
import { mode } from '@chakra-ui/theme-tools';

import { convertHexToRGBAString } from '../../_helpers';
import { COLORS } from '../../theme';

import { TabsProps } from './Tabs';

const enclosedFontSizeMap = {
  sm: 'sm',
  md: 'md',
  lg: 'lg'
};
const lineBorderRadiusMap = {
  sm: '6px',
  md: '8px',
  lg: '10px'
};
const enclosedBorderRadiusMap = {
  sm: '16px',
  md: '18px',
  lg: '22px'
};
const enclosedTabHeightMap = {
  sm: '28px',
  md: '36px',
  lg: '44px'
};
const tabIconSizeMap = {
  sm: 'sm',
  md: 'md',
  lg: 'lg'
};

const TabsTheme: ComponentMultiStyleConfig = {
  parts: ['root', 'tab', 'tablist', 'tabpanel'],
  baseStyle: {
    tab: {
      fontWeight: 'normal',
      background: 'transparent',
      whiteSpace: 'nowrap',
      ':not(:focus-visible)': {
        boxShadow: 'none',
        outline: 'none'
      },
      transitionDuration: '0s'
    },
    tablist: {
      alignItems: 'center'
    },
    tabpanel: {
      padding: '24px'
    }
  },
  sizes: {
    sm: {
      tab: {
        height: '24px',
        fontSize: 'md',
        padding: '0px 16px',
        [`::before`]: {
          padding: '0px 16px',
          height: '29px'
        },
        _selected: {
          [`::before`]: {
            padding: '0px 16px',
            height: '29px'
          }
        }
      },
      tablist: {
        height: '32px'
      },
      tabListIconLeft: {
        background: 'linear-gradient(90deg, #FFFFFF 32px, rgba(255, 255, 255, 0) 100%)',
        width: '40px'
      },
      tabListIconRight: {
        width: '40px'
      }
    },
    md: {
      tab: {
        height: '32px',
        fontSize: 'lg',
        padding: '0px 24px',
        [`::before`]: {
          padding: '0px 24px',
          height: '37px'
        },
        _selected: {
          [`::before`]: {
            padding: '0px 24px',
            height: '37px'
          }
        }
      },
      tablist: {
        height: '40px'
      },
      tabListIconLeft: {
        background: 'linear-gradient(90deg, #FFFFFF 40px, rgba(255, 255, 255, 0) 100%)',
        width: '48px'
      },
      tabListIconRight: {
        width: '48px'
      }
    },
    lg: {
      tab: {
        height: '40px',
        fontSize: 'xl',
        padding: '0px 32px',
        [`::before`]: {
          padding: '0px 32px',
          height: '45px'
        },
        _selected: {
          [`::before`]: {
            padding: '0px 32px',
            height: '45px'
          }
        }
      },
      tablist: {
        height: '48px'
      },
      tabListIconLeft: {
        background: 'linear-gradient(90deg, #FFFFFF 44px, rgba(255, 255, 255, 0) 100%)',
        width: '52px'
      },
      tabListIconRight: {
        width: '52px'
      }
    }
  },
  variants: {
    line: (props: StyleFunctionProps) => ({
      tab: {
        color: mode('black.lighter', 'silver.lighter')(props),
        borderBottom: '0px',
        borderRadius: lineBorderRadiusMap[props.size],
        position: 'relative',
        marginBottom: '1px',
        padding: '0px 16px',
        [`::before`]: {
          content: '""',
          borderBottomWidth: '1px',
          borderColor: props.borderColor ? props.borderColor : mode('silver.base', 'grey.base')(props),
          position: 'absolute',
          padding: '0px 16px',
          left: 0,
          right: 0,
          top: 0
        },

        _selected: {
          color: mode('black.lighter', 'silver.lighter')(props),
          fontWeight: 'medium',
          [`::before`]: {
            content: '""',
            borderBottomWidth: '2px',
            borderColor: mode('black.lighter', 'silver.lighter')(props),
            position: 'absolute',
            padding: '0px 16px',
            left: 0,
            right: 0,
            top: 0
          }
        },
        _active: {
          color: mode('black.lighter', 'silver.lighter')(props),
          background: 'none'
        },
        _hover: {
          color: mode('black.lighter', 'silver.lighter')(props),

          _light: {
            backgroundColor: convertHexToRGBAString(COLORS.black.base, 0.04),
            _disabled: {
              backgroundColor: 'transparent'
            }
          },
          _dark: {
            backgroundColor: convertHexToRGBAString(COLORS.silver.lightest, 0.08),
            _disabled: {
              backgroundColor: 'transparent'
            }
          }
        },
        _focusVisible: {
          _light: {
            boxShadow: `0px 0px 0px 1px ${COLORS.silver.lightest}, 0px 0px 0px 4px ${COLORS.blue.lighter}`,
            backgroundColor: convertHexToRGBAString(COLORS.black.base, 0.04)
          },
          _dark: {
            boxShadow: `0px 0px 0px 1px ${COLORS.black.base}, 0px 0px 0px 4px ${COLORS.blue.lighter}`,
            backgroundColor: convertHexToRGBAString(COLORS.silver.lightest, 0.08)
          }
        }
      },
      tablist: {
        borderBottom: '1px solid',
        borderColor: props.borderColor ? props.borderColor : mode('silver.base', 'grey.base')(props)
      },
      tabListIconSize: {
        boxSize: tabIconSizeMap[props.size]
      },
      tabListIconLeft: {
        display: 'flex',
        alignItems: 'center',
        background: `linear-gradient(90deg, ${mode(
          COLORS.silver.lightest,
          COLORS.black.base
        )(props)} calc(100% - 8px), rgba(255, 255, 255, 0) 100%)`,
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0
      },
      tabListIconRight: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'end',
        background: `linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, ${mode(
          COLORS.silver.lightest,
          COLORS.black.base
        )(props)} 8px)`,
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0
      }
    }),
    enclosed: (props: TabsProps & StyleFunctionProps) => ({
      tab: {
        flex: 'auto',
        color: mode('grey.base', 'grey.lightest')(props),
        fontSize: enclosedFontSizeMap[props.size],
        height: enclosedTabHeightMap[props.size],
        borderRadius: enclosedBorderRadiusMap[props.size],
        _selected: {
          color: mode('black.lighter', 'silver.lighter')(props),
          background: mode('silver.lightest', 'grey.base')(props),
          border: '0',
          boxShadow: mode(
            `0px 0px 0px 0.5px ${convertHexToRGBAString(COLORS.black.base, 0.08)}, 0px 2px 2px ${convertHexToRGBAString(
              COLORS.black.base,
              0.04
            )}, 0px 2px 4px -2px ${convertHexToRGBAString(COLORS.black.base, 0.08)}`,
            `0px 0px 0px 0.5px ${convertHexToRGBAString(COLORS.black.base, 0.24)}, 0px 2px 2px ${convertHexToRGBAString(
              COLORS.black.base,
              0.16
            )}, 0px 2px 4px -2px ${convertHexToRGBAString(COLORS.black.base, 0.24)};`
          )(props),
          fontWeight: 'medium'
        },
        _hover: {
          color: mode('black.lighter', 'silver.lighter')(props),
          ':not([aria-selected="false"])': {
            color: mode('black.lighter', 'silver.lighter')(props)
          },
          _selected: {
            backgroundColor: mode('silver.lighter', 'black.lightest')(props)
          }
        },
        _disabled: {
          color: mode('grey.lighter', 'grey.light')(props),
          backgroundColor: 'transparent',
          cursor: 'not-allowed',
          _selected: {
            boxShadow: 'none'
          },
          _hover: {
            color: mode('grey.lighter', 'grey.light')(props),
            backgroundColor: 'transparent'
          }
        },
        _focusVisible: {
          _light: {
            boxShadow: `0px 0px 0px 1px ${COLORS.silver.lightest}, 0px 0px 0px 4px ${COLORS.blue.lighter}`,
            backgroundColor: COLORS.silver.lighter
          },
          _dark: {
            boxShadow: `0px 0px 0px 1px ${COLORS.black.base}, 0px 0px 0px 4px ${COLORS.blue.lighter}`,
            backgroundColor: COLORS.black.lightest
          }
        }
      },
      tablist: {
        alignItems: 'flex-start',
        background: mode('silver.light', 'black.lighter')(props),
        border: '0',
        borderRadius: '40px',
        padding: '2px'
      }
    })
  },
  defaultProps: {
    colorScheme: undefined
  }
};

export default TabsTheme;
